import { useMemo, useRef, useState, type SyntheticEvent } from "react";

import { useQuery } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { docUrl } from "../../lib/docLinks";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { unicodeCaseFold } from "../../lib/unicodeCaseFold";
import { useVariableDeclarations } from "../../lib/useVariableDeclarations";
import {
  groupDeclarationsByKey,
  variableDeclarationsFingerprint,
} from "../../lib/variableCascade";
import { scanVariableTokens } from "../../lib/variableTokens";
import {
  Button,
  CodeEditor,
  HelpTip,
  Inline,
  InlineError,
  RouterLink,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "../../ui";
import { AuthoringLeaveGuard } from "../AuthoringLeaveGuard";
import {
  InsertVariableMenu,
  insertVariableToken,
  type InsertableVariable,
} from "../variables/InsertVariableMenu";
import {
  rewritePatternTokens,
  variablePatternAliases,
} from "../../lib/variablePatterns";
import { ScenarioTokenRow } from "./ScenarioTokenRow";
import { ScenarioPreviewPanel } from "./ScenarioPreviewPanel";

type ScenarioResponse = components["schemas"]["ScenarioResponse"];
type ScenarioVoiceBehavior = NonNullable<
  ScenarioResponse["current_revision"]["environment"]["voice_behavior"]
>;
type DraftVoiceBehavior = components["schemas"]["VoiceBehavior"];
export interface ScenarioAgentOption {
  id: string;
  label: string;
}

export interface ScenarioFormSubmission {
  // The owning agent: the scoped agent, or the unscoped create form's pick.
  agentId: string;
  displayName: string;
  environment: components["schemas"]["ScenarioEnvironment"];
  goal?: string;
  instruction: string;
}

export interface ScenarioEditorProps {
  // Owning-agent choices for the unscoped create form; omit to hide the
  // Select (agent-scoped create, or edit — a scenario never changes agents).
  // Mirrors the persona editor.
  agentOptions?: ScenarioAgentOption[];
  error: string | null;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (submission: ScenarioFormSubmission) => Promise<void>;
  scenario: ScenarioResponse | null;
  // Pre-fills the owning agent when the page carries an agent scope.
  scopedAgentId?: string;
}

function stringContent(scenario: ScenarioResponse | null, key: string): string {
  const value = scenario?.current_revision.content[key];
  const references = (scenario?.current_revision.references ?? []).flatMap(
    (reference) =>
      reference.variable_id === null
        ? []
        : [
            {
              id: reference.variable_id,
              key: reference.key,
              scope_type: reference.scope_type ?? "scenario",
            },
          ],
  );
  return typeof value === "string"
    ? rewritePatternTokens(
        value,
        variablePatternAliases(references).stored,
        true,
      )
    : "";
}

function draftVoiceBehavior(
  behavior: ScenarioVoiceBehavior | null | undefined,
): DraftVoiceBehavior | null {
  if (behavior === null || behavior === undefined) return null;
  const authored = Object.fromEntries(
    Object.entries(behavior).filter(([, value]) => value !== null),
  ) as DraftVoiceBehavior;
  return Object.keys(authored).length === 0 ? null : authored;
}

export function ScenarioEditor(props: ScenarioEditorProps) {
  const subjectKey = props.scenario?.id ?? `new:${props.scopedAgentId ?? ""}`;
  return <ScenarioEditorForm key={subjectKey} {...props} />;
}

function ScenarioEditorForm({
  agentOptions,
  error,
  isPending,
  onCancel,
  onSubmit,
  scenario,
  scopedAgentId,
}: ScenarioEditorProps) {
  const [agentId, setAgentId] = useState(
    scenario?.agent_id ?? scopedAgentId ?? "",
  );
  const [displayName, setDisplayName] = useState(scenario?.display_name ?? "");
  const [instruction, setInstruction] = useState(() =>
    stringContent(scenario, "instruction"),
  );
  const [goal, setGoal] = useState(() => stringContent(scenario, "goal"));
  const [localError, setLocalError] = useState<string | null>(null);
  const instructionRef = useRef<HTMLTextAreaElement>(null);
  const goalRef = useRef<HTMLTextAreaElement>(null);
  const showAgentSelect = scenario === null && agentOptions !== undefined;
  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) throw new ApiRequestError(error);
      return data;
    },
  });
  const cascade = useVariableDeclarations(agentId);
  const availableVariables = (cascade.data ?? []).filter((variable) =>
    variable.scope_type === "agent"
      ? variable.scope_id === agentId
      : variable.scope_type === "scenario" &&
        variable.scope_id === scenario?.id,
  );
  const knownVariables = [
    ...availableVariables,
    ...(scenario?.current_revision.references ?? []).flatMap((reference) =>
      reference.variable_id === null ||
      availableVariables.some(
        (variable) => variable.id === reference.variable_id,
      )
        ? []
        : [
            {
              id: reference.variable_id,
              key: reference.key,
              scope_type: reference.scope_type ?? "scenario",
            },
          ],
    ),
  ];
  const aliases = variablePatternAliases(knownVariables);
  const displayedInstruction = rewritePatternTokens(
    instruction,
    aliases.display,
  );
  const displayedGoal = rewritePatternTokens(goal, aliases.display);
  // A stable draft identity: the preview panel debounces on it, so a fresh
  // object per render would refetch on every unrelated state change.
  const previewDraft = useMemo(
    () =>
      scenario === null
        ? null
        : {
            content: {
              instruction,
              ...(goal.length === 0 ? {} : { goal }),
            },
            environment: {
              background: scenario.current_revision.environment.background,
              tool_mocks:
                scenario.current_revision.environment.tool_mocks ?? [],
              voice_behavior: draftVoiceBehavior(
                scenario.current_revision.environment.voice_behavior,
              ),
            },
            mode: "generative" as const,
          },
    [goal, instruction, scenario],
  );

  // OPEN(scripted-divergence-policy): scripted authoring stops at this
  // read-only seam until the active docs remove the named fork.
  if (scenario?.current_revision.mode === "scripted") {
    return (
      <Stack gap={4}>
        <Text variant="muted">Scripted scenarios are read-only.</Text>
        <CodeEditor
          label="Script"
          readOnly
          value={JSON.stringify(scenario.current_revision.content, null, 2)}
          onChange={() => undefined}
        />
        <Inline>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Close
          </Button>
        </Inline>
      </Stack>
    );
  }

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextDisplayName = displayName.trim();
    const nextInstruction = instruction.trim();
    if (
      agentId.length === 0 ||
      nextDisplayName.length === 0 ||
      nextInstruction.length === 0
    ) {
      setLocalError("Agent, display name and instruction are required.");
      return;
    }
    setLocalError(null);
    try {
      await onSubmit({
        agentId,
        displayName: nextDisplayName,
        environment: {
          background: scenario?.current_revision.environment.background ?? null,
          // Preserve API-authored mocks until a compatible connection supports
          // tool-mock authoring in the console.
          tool_mocks: scenario?.current_revision.environment.tool_mocks ?? [],
          voice_behavior: draftVoiceBehavior(
            scenario?.current_revision.environment.voice_behavior,
          ),
        },
        instruction: nextInstruction,
        ...(goal.trim().length > 0 ? { goal: goal.trim() } : {}),
      });
    } catch (caught) {
      setLocalError(requestErrorDescription(caught));
    }
  }

  const tokens = scanVariableTokens(displayedInstruction, displayedGoal);
  const storedTokens = new Set(scanVariableTokens(instruction, goal));
  const referencesByKey = new Map(
    (scenario?.current_revision.references ?? []).map((reference) => [
      unicodeCaseFold(reference.key),
      reference,
    ]),
  );
  const declarationGroups = groupDeclarationsByKey(cascade.data ?? []);
  const declarationRevision = variableDeclarationsFingerprint(
    cascade.data ?? [],
  );
  function insertVariable(
    variable: InsertableVariable,
    field: "instruction" | "goal",
  ) {
    const input =
      field === "instruction" ? instructionRef.current : goalRef.current;
    const current =
      field === "instruction" ? displayedInstruction : displayedGoal;
    const inserted = insertVariableToken(
      current,
      aliases.display.get(variable.id) ?? variable.id,
      input?.selectionStart,
      input?.selectionEnd,
    );
    (field === "instruction" ? setInstruction : setGoal)(
      rewritePatternTokens(inserted.value, aliases.stored, true),
    );
    window.requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(inserted.cursor, inserted.cursor);
    });
  }
  const isDirty =
    agentId !== (scenario?.agent_id ?? scopedAgentId ?? "") ||
    displayName !== (scenario?.display_name ?? "") ||
    instruction !== stringContent(scenario, "instruction") ||
    goal !== stringContent(scenario, "goal");

  return (
    <>
      <AuthoringLeaveGuard
        isDirty={isDirty && !isPending}
        isPending={isPending}
        subject="scenario editor"
      />
      <Stack gap={4}>
        <form
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
        >
          <Stack gap={4}>
            {showAgentSelect ? (
              <Select
                label="Agent"
                required
                value={agentId}
                onChange={(event) => {
                  setAgentId(event.currentTarget.value);
                }}
              >
                <option disabled value="">
                  Select an agent
                </option>
                {agentOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </Select>
            ) : null}
            <TextInput
              disabled={scenario !== null}
              description={
                scenario
                  ? "Scenario names cannot be changed after creation."
                  : undefined
              }
              label="Display name"
              required
              value={displayName}
              onChange={(event) => {
                setDisplayName(event.currentTarget.value);
              }}
            />
            <Textarea
              help={
                <HelpTip
                  docHref={docUrl("guides/author-a-scenario")}
                  docLabel="Author a Scenario"
                  label="About caller instructions"
                >
                  {
                    "Instructions guide the simulated caller's conversation. They are prompt context, not a fixed script, and variables are passed separately."
                  }
                </HelpTip>
              }
              label="Caller instructions"
              ref={instructionRef}
              required
              rows={7}
              value={displayedInstruction}
              onChange={(event) => {
                setInstruction(
                  rewritePatternTokens(
                    event.currentTarget.value,
                    aliases.stored,
                    true,
                  ),
                );
              }}
            />
            <InsertVariableMenu
              label="Insert instruction variable"
              variables={availableVariables}
              isPending={cascade.isPending && agentId.length > 0}
              error={cascade.isError}
              disabled={isPending || agentId.length === 0}
              onRetry={() => void cascade.refetch()}
              onInsert={(variable) => {
                insertVariable(variable, "instruction");
              }}
            />
            <Textarea
              help={
                <HelpTip
                  docHref={docUrl("guides/author-a-scenario")}
                  docLabel="Author a Scenario"
                  label="About caller goals"
                >
                  {
                    "The goal is sent to the persona model with the instructions. It can guide the conversation and its ending, but only Markers and Assertions decide pass or fail."
                  }
                </HelpTip>
              }
              label="Caller goal"
              ref={goalRef}
              rows={3}
              value={displayedGoal}
              onChange={(event) => {
                setGoal(
                  rewritePatternTokens(
                    event.currentTarget.value,
                    aliases.stored,
                    true,
                  ),
                );
              }}
            />
            <InsertVariableMenu
              label="Insert goal variable"
              variables={availableVariables}
              isPending={cascade.isPending && agentId.length > 0}
              error={cascade.isError}
              disabled={isPending || agentId.length === 0}
              onRetry={() => void cascade.refetch()}
              onInsert={(variable) => {
                insertVariable(variable, "goal");
              }}
            />
            <Stack gap={2}>
              <Inline align="center" justify="between" gap={2}>
                <Inline align="center" gap={2}>
                  <Text variant="caps">Variables</Text>
                  <HelpTip
                    label="About Scenario variables"
                    docHref={docUrl("guides/use-variables-in-simulations")}
                    docLabel="Use variables in simulations"
                  >
                    {
                      "Insert an Agent constant or this Scenario's input into instructions or goals. Prompt Scenarios receive resolved values as caller context; braces are not replaced in their text. Create declarations in a new tab, then return here to insert them."
                    }
                  </HelpTip>
                </Inline>
                {agentId.length > 0 ? (
                  <RouterLink
                    params={{ agentId }}
                    search={{
                      new: 1,
                      ...(scenario === null
                        ? {}
                        : { scope_type: "scenario", scope_id: scenario.id }),
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    to="/agents/$agentId/variables"
                  >
                    Declare variable
                  </RouterLink>
                ) : null}
              </Inline>
              {agentId.length === 0 ? (
                <Text variant="muted">Choose an Agent to load variables.</Text>
              ) : cascade.isPending ? (
                <Skeleton label="Loading variables" shape="detail-field" />
              ) : cascade.isError ? (
                <Inline gap={2}>
                  <InlineError>
                    {requestErrorDescription(
                      cascade.error,
                      "Variables failed to load.",
                    )}
                  </InlineError>
                  <Button
                    size="sm"
                    type="button"
                    variant="secondary"
                    onClick={() => void cascade.refetch()}
                  >
                    Retry variables
                  </Button>
                </Inline>
              ) : tokens.length === 0 ? (
                <Text variant="muted">No variables inserted</Text>
              ) : null}
              {scenario === null && agentId.length > 0 ? (
                <Text variant="muted">
                  Save this Scenario before creating its own inputs.
                </Text>
              ) : null}
              {(!cascade.isPending && !cascade.isError ? tokens : []).map(
                (token) => {
                  const variable = knownVariables.find(
                    (item) =>
                      item.id === token ||
                      item.id === aliases.stored.get(unicodeCaseFold(token)),
                  );
                  const key = variable?.key ?? token;
                  const reference =
                    variable !== undefined && storedTokens.has(variable.id)
                      ? {
                          key,
                          variable_id: variable.id,
                          scope_type: variable.scope_type,
                          warning: null,
                        }
                      : referencesByKey.get(unicodeCaseFold(token));
                  return (
                    <ScenarioTokenRow
                      agentId={scenario?.agent_id ?? agentId}
                      group={declarationGroups.get(unicodeCaseFold(key)) ?? []}
                      key={unicodeCaseFold(token)}
                      keyRulesError={me.isError}
                      keyRulesPending={me.isPending}
                      reference={reference}
                      reservedKeys={me.data?.variable_key_rules.reserved}
                      scenarioId={scenario?.id}
                      token={key}
                    />
                  );
                },
              )}
            </Stack>
            {scenario && previewDraft ? (
              <ScenarioPreviewPanel
                declarationsRefreshing={cascade.isFetching}
                displayInstruction={displayedInstruction}
                declarationRevision={declarationRevision}
                draft={previewDraft}
                scenario={scenario}
              />
            ) : null}
            {(localError ?? error) ? (
              <InlineError>{localError ?? error}</InlineError>
            ) : null}
            <Inline justify="end">
              <Button
                disabled={isPending}
                type="button"
                variant="secondary"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button disabled={isPending} type="submit">
                {isPending ? "Saving..." : "Save"}
              </Button>
            </Inline>
          </Stack>
        </form>
      </Stack>
    </>
  );
}
